// Smart Daara - Certificate Component
import React, { useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Download, Share2, Award, CheckCircle } from 'lucide-react';

// Smart Daara Logo
const LOGO_URL = '/logo.png';

export default function Certificate({ 
  certificate,
  schoolName = 'Smart Daara'
}) {
  const { language } = useLanguage();
  const certificateRef = useRef(null);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDownload = () => {
    // In production, this would generate a PDF
    window.print();
  };

  const handleShare = async () => {
    const shareData = {
      title: language === 'fr' ? 'Mon Certificat Smart Daara' : 'My Smart Daara Certificate',
      text: language === 'fr' 
        ? `J'ai obtenu mon certificat pour "${certificate?.course_title_fr || certificate?.course_title}" sur Smart Daara!`
        : `I earned my certificate for "${certificate?.course_title}" on Smart Daara!`,
      url: `${window.location.origin}/verify/${certificate?.certificate_number}`
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareData.url);
      alert(language === 'fr' ? 'Lien copié!' : 'Link copied!');
    }
  };

  if (!certificate) return null;

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex justify-end gap-2 print:hidden">
        <Button variant="outline" onClick={handleShare}>
          <Share2 className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Partager' : 'Share'}
        </Button>
        <Button onClick={handleDownload} className="bg-emerald-700 hover:bg-emerald-800">
          <Download className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Télécharger' : 'Download'}
        </Button>
      </div>

      {/* Certificate */}
      <div 
        ref={certificateRef}
        className="bg-white border-4 border-double border-amber-500 rounded-lg p-8 md:p-12 shadow-lg print:shadow-none print:border-8"
        style={{ 
          backgroundImage: 'linear-gradient(45deg, #fefce8 0%, #ffffff 50%, #fefce8 100%)',
          minHeight: '500px'
        }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Smart Daara" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl md:text-4xl font-outfit font-bold text-emerald-800">
            {language === 'fr' ? 'CERTIFICAT DE RÉUSSITE' : 'CERTIFICATE OF COMPLETION'}
          </h1>
          <div className="mt-2 flex items-center justify-center gap-2 text-amber-600">
            <div className="h-px w-16 bg-amber-500" />
            <Award className="w-6 h-6" />
            <div className="h-px w-16 bg-amber-500" />
          </div>
        </div>

        {/* Content */}
        <div className="text-center space-y-6 mb-8">
          <p className="text-slate-600 text-lg">
            {language === 'fr' ? 'Ceci certifie que' : 'This certifies that'}
          </p>
          
          <h2 className="text-3xl md:text-4xl font-outfit font-bold text-slate-900 border-b-2 border-emerald-600 pb-2 inline-block px-8">
            {certificate.student_name}
          </h2>
          
          <p className="text-slate-600 text-lg">
            {language === 'fr' 
              ? 'a complété avec succès le cours'
              : 'has successfully completed the course'}
          </p>
          
          <h3 className="text-2xl font-semibold text-emerald-700">
            "{certificate.course_title_fr || certificate.course_title}"
          </h3>
          
          <p className="text-slate-600">
            {language === 'fr' ? 'le' : 'on'} {formatDate(certificate.completion_date)}
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-end mt-12 pt-8 border-t border-slate-200">
          <div className="text-center">
            <div className="w-32 h-px bg-slate-400 mb-2" />
            <p className="text-sm text-slate-500">{schoolName}</p>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-600">
                {language === 'fr' ? 'Vérifié' : 'Verified'}
              </span>
            </div>
            <p className="text-xs text-slate-500 font-mono">
              N° {certificate.certificate_number}
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-32 h-px bg-slate-400 mb-2" />
            <p className="text-sm text-slate-500">Smart Daara</p>
          </div>
        </div>

        {/* Verification URL */}
        <div className="mt-6 text-center print:mt-8">
          <p className="text-xs text-slate-400">
            {language === 'fr' ? 'Vérifiez ce certificat sur' : 'Verify this certificate at'}: {window.location.origin}/verify/{certificate.certificate_number}
          </p>
        </div>
      </div>
    </div>
  );
}
